import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import seoConfig from '../data/seo.json';

const SEO = ({
  page = 'home',
  title: customTitle,
  description: customDescription,
  image: customImage,
  path: customPath,
  keywords: customKeywords,
  article = false,
  publishedTime,
  modifiedTime,
  author = 'Nikhil Bhatt',
  tags = [],
}) => {
  const { pathname } = useLocation();
  const { default: DEFAULT_SEO, pages: PAGE_SEO } = seoConfig;
  const pageSEO = PAGE_SEO[page] || {};

  // Get SEO values with fallbacks
  const title = customTitle || pageSEO.title || DEFAULT_SEO.title;
  const description = customDescription || pageSEO.description || DEFAULT_SEO.description;
  const image = customImage || `${DEFAULT_SEO.siteUrl}${pageSEO.image || DEFAULT_SEO.defaultImage}`;
  const path = customPath || pageSEO.path || pathname;
  const keywords = customKeywords
    ? `${customKeywords}, ${DEFAULT_SEO.keywords}`
    : `${pageSEO.keywords || ''}, ${DEFAULT_SEO.keywords}`;
  const url = `${DEFAULT_SEO.siteUrl}${path}`;
  const type = article ? 'article' : 'website';
  const siteName = DEFAULT_SEO.siteName || 'Nikhil Bhatt';

  // Generate schema.org JSON-LD
  const schemaOrgJSONLD = [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Nikhil Bhatt',
      'url': DEFAULT_SEO.siteUrl,
      'sameAs': Object.values(DEFAULT_SEO.socialProfiles || {}),
      'jobTitle': 'Full Stack Developer',
      'worksFor': {
        '@type': 'Organization',
        'name': 'Freelance',
      },
      'knowsAbout': DEFAULT_SEO.skills || [],
      'description': description,
    },
    // Website Schema
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'url': DEFAULT_SEO.siteUrl,
      'name': DEFAULT_SEO.siteName,
      'description': DEFAULT_SEO.description,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': `${DEFAULT_SEO.siteUrl}/blogs?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },

    // Add WebSite schema
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'url': DEFAULT_SEO.siteUrl,
      'name': siteName,
      'description': DEFAULT_SEO.description,
      'publisher': {
        '@type': 'Person',
        'name': 'Nikhil Bhatt',
      },
    },
  ];

  // Add Article schema for blog posts
  if (article) {
    schemaOrgJSONLD.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': title,
      'description': description,
      'image': image,
      'author': {
        '@type': 'Person',
        'name': author,
      },
      'publisher': {
        '@type': 'Organization',
        'name': siteName,
        'logo': {
          '@type': 'ImageObject',
          'url': `${DEFAULT_SEO.siteUrl}/logo192.png`,
        },
      },
      'datePublished': publishedTime,
      'dateModified': modifiedTime || publishedTime,
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': url,
      },
      'keywords': keywords,
      'articleSection': 'Technology',
      'inLanguage': 'en-US',
      'wordCount': '1000',
      'articleBody': description,
    });
  }

  // Add BreadcrumbList schema
  schemaOrgJSONLD.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': DEFAULT_SEO.siteUrl,
      },
      {
        '@type': 'ListItem',
        'name': 'Blogs',
        'position': 2,
        'item': url,
      },
    ],
  });

  // Generate meta tags array
  const metaTags = [
    // Basic Meta
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },

    // Open Graph / Facebook
    { property: 'og:type', content: type },
    { property: 'og:url', content: url },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: image },
    { property: 'og:site_name', content: siteName },

    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:creator', content: DEFAULT_SEO.twitterHandle },
    { name: 'twitter:site', content: DEFAULT_SEO.twitterHandle },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },

    // Canonical URL
    { rel: 'canonical', href: url },

    // Robots
    { name: 'robots', content: 'index, follow' },
  ];

  // Add article-specific meta tags
  if (article) {
    metaTags.push(
      { property: 'article:published_time', content: publishedTime },
      { property: 'article:modified_time', content: modifiedTime },
      { property: 'article:author', content: author },
      { property: 'article:section', content: 'Technology' },
      ...tags.map(tag => ({
        property: 'article:tag',
        content: tag,
      })),
    );
  }

  return (
    <Helmet
      title={title}
      titleTemplate={DEFAULT_SEO.titleTemplate}
      defaultTitle={DEFAULT_SEO.defaultTitle}
      meta={metaTags}
    >
      <html lang="en" />
      <title>{title}</title>

      {/* Render all meta tags from metaTags array */}
      {metaTags.map(({ name, property, content }, index) => (
        <meta
          key={`meta-${index}`}
          {...(name && { name })}
          {...(property && { property })}
          content={content}
        />
      ))}

      {/* Additional meta tags */}
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
      {article && modifiedTime && <meta property="og:article:modified_time" content={modifiedTime} />}
      {article && tags.map((tag, index) => (
        <meta key={index} property="og:article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      <meta name="application-name" content={siteName} />

      {/* Favicons */}
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#2563eb" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
    </Helmet>
  );
};

export default SEO;
